import { CloseIcon } from '@arnold/common';
import { getLocale } from '@arnold/core';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReportDefinitionsNewForSurveyQuery,
  useReportDefinitionsNewForTopicQuery,
} from '../../../../generated/hooks';
import type { NewReport } from '../../hooks/useNewReport';
import { useDefinition } from '../ReportDefinitionContext';
import { FloatingCard, FloatingCardBody, FloatingCardHeader, ToolButton } from './editorStyles';
import { Action, Bar, ErrorText, Hint, Row } from './styles';

type HistoryVersion = {
  id: string;
  name?: string | null;
  createdAt: string;
  createdBy?: string | null;
  isPrimary: boolean;
};

function historyVersions(surveyVersions: HistoryVersion[], topicVersions: HistoryVersion[]) {
  const byId = new Map<string, HistoryVersion & { isPrimaryForSurvey: boolean; isPrimaryForTopic: boolean }>();
  surveyVersions.forEach((version) => {
    byId.set(version.id, { ...version, isPrimaryForSurvey: version.isPrimary, isPrimaryForTopic: false });
  });
  topicVersions.forEach((version) => {
    const existing = byId.get(version.id);
    if (existing) {
      existing.isPrimaryForTopic = version.isPrimary;
      return;
    }
    byId.set(version.id, { ...version, isPrimaryForSurvey: false, isPrimaryForTopic: version.isPrimary });
  });
  return [...byId.values()].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() || Number(b.id) - Number(a.id),
  );
}

function primaryLabel(
  version: { isPrimaryForSurvey: boolean; isPrimaryForTopic: boolean },
  translate: (key: string) => string,
) {
  if (version.isPrimaryForSurvey && version.isPrimaryForTopic) return translate('definition.primaryForBoth');
  if (version.isPrimaryForSurvey) return translate('definition.primaryForSurvey');
  if (version.isPrimaryForTopic) return translate('definition.primaryForTopic');
  return null;
}

/** Saved versions for this survey and its topic, floating above the dock. */
export function HistoryCard({ report, onClose }: { report: NewReport; onClose: () => void }) {
  const { t, i18n } = useTranslation('newReport');
  const controller = useDefinition();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const skip = !controller.canEdit || !report.rawData;
  const surveyId = report.rawData?.surveyId ?? '';
  const surveyHistory = useReportDefinitionsNewForSurveyQuery({
    variables: { surveyId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const topicHistory = useReportDefinitionsNewForTopicQuery({
    variables: { surveyId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const versions = historyVersions(
    surveyHistory.data?.reportDefinitionsNewForSurvey ?? [],
    topicHistory.data?.reportDefinitionsNewForTopic ?? [],
  );
  const loading = (surveyHistory.loading || topicHistory.loading) && versions.length === 0;
  return (
    <FloatingCard>
      <FloatingCardHeader>
        <h3>{t('definition.history')}</h3>
        <ToolButton type="button" onClick={onClose} title={t('definition.close')}>
          <CloseIcon />
        </ToolButton>
      </FloatingCardHeader>
      <FloatingCardBody>
        <Hint style={{ padding: '10px 0' }}>{t('definition.historyHelp')}</Hint>
        {loading && <Hint>{t('definition.loading')}</Hint>}
        {(surveyHistory.error || topicHistory.error) && <ErrorText>{t('definition.operationFailed')}</ErrorText>}
        {!loading && versions.length === 0 && <Hint>{t('definition.noVersions')}</Hint>}
        {versions.map((version) => {
          const date = new Date(version.createdAt);
          const exact = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'medium' }).format(
            date,
          );
          const previewing = controller.previewId === version.id;
          const primary = primaryLabel(version, t);
          return (
            <Row key={version.id}>
              <Bar>
                <strong style={{ flex: 1 }}>{version.name || t('definition.version', { id: version.id })}</strong>
                {primary && <Hint>{primary}</Hint>}
              </Bar>
              <Hint>
                {version.createdBy || t('definition.deletedUser')} ·{' '}
                <span title={exact}>
                  {now - date.getTime() < 10 * 86400000
                    ? formatDistanceToNow(date, { addSuffix: true, locale: getLocale(i18n.language) })
                    : exact}
                </span>
              </Hint>
              <Bar style={{ marginTop: 10 }}>
                <Action
                  active={previewing}
                  disabled={controller.busy}
                  onClick={() => (previewing ? controller.exitPreview() : controller.preview(version.id))}
                >
                  {t(previewing ? 'definition.exitPreview' : 'definition.preview')}
                </Action>
                <Action
                  disabled={controller.busy || version.isPrimaryForSurvey}
                  onClick={() => controller.setPrimaryForSurvey(version.id)}
                >
                  {t('definition.makePrimaryForSurvey')}
                </Action>
                <Action
                  disabled={controller.busy || version.isPrimaryForTopic}
                  onClick={() => controller.setPrimaryForTopic(version.id)}
                >
                  {t('definition.makePrimaryForTopic')}
                </Action>
              </Bar>
            </Row>
          );
        })}
      </FloatingCardBody>
    </FloatingCard>
  );
}
